import React, { useCallback, useContext, useRef, useState } from "react";
import styles from "./ReKindleButton.module.scss";
import TranslationsContext from "src/contexts/TranslationsContext";
import { newMetricsWithContext } from "src/utils/metricsUtils";
import { NativeAPI } from "src/utils/deviceUtils";
import WorkflowContext from "src/contexts/WorkflowContext";
import focusHelper from "src/utils/focusHelper";
import { getDetailsUrl } from "src/utils/asinUtils";
import { getCurrentBaseUrl } from "src/utils/urlUtils";
import useImpressionCallback from "src/utils/useImpressionCallback";
import DeviceContext from "src/contexts/DeviceContext";
import { LinkOutSSOImpl } from "@amzn/identity-app-to-browser-sso-js-sdk";
import Spinner from "../Spinner/Spinner";

type PropTypes = {
    metadata: QuickViewAsinMetadata;
    isMiniHeader?: boolean;
};

const ReKindleButton: React.FC<PropTypes> = ({ metadata, isMiniHeader }: PropTypes) => {
    const deviceContext = useContext(DeviceContext);
    const translations = useContext(TranslationsContext);
    const workflowContext = useContext(WorkflowContext);
    const a11yTarget = useRef<HTMLButtonElement>(null);
    const isReKindle2 = deviceContext.enableRekindle2;
    const buttonName = isReKindle2 ? "ReKindle2Button" : "ReKindleButton";
    const metrics = newMetricsWithContext(isMiniHeader ? `${buttonName}.miniHeader` : buttonName, metadata.asin);
    const isTeenAccount = deviceContext.isTeenAccount;

    // Loading state to show spinner during SSO authentication flow
    const [isLoading, setIsLoading] = useState(false);

    const requestA11yFocus = useCallback(() => {
        focusHelper.requestFocus(a11yTarget.current);
    }, [a11yTarget]);

    const showReKindleErrorCX = (): void => {
        metrics.recordMetric(`${buttonName}.ErrorCX`, 1);
        workflowContext.showReKindleError(requestA11yFocus);
    }

    // ReKindle link-out without SSO
    const callReKindleAPI = (url: string): boolean => {
        if (deviceContext.ssoConfig) {
            metrics.recordMetric(`${buttonName}.LinkoutSSO.Fallback`, 1);
        }
        
        metrics.recordMetric(`${buttonName}.TraditionalLinkoutFallback`, 1);
        return (NativeAPI.openWebPageExternal(url) || NativeAPI.openReKindleInterstitial(metadata));
    }
    
    const tryLinkOutWithSSO = async (url: string): Promise<boolean> => {
        if (!deviceContext.ssoConfig) {
            metrics.recordMetric(`${buttonName}.SSOInEligible.NoConfig`, 1);
            return false;
        }

        let isLinkOutWithSsoSuccessful = false;
        setIsLoading(true);
        
        try {
            metrics.recordMetric(`${buttonName}.LinkoutSSO.Attempt`, 1);
            
            const linkOutSSOImpl = new LinkOutSSOImpl();
            const linkoutSSOResponse = await linkOutSSOImpl.initLinkOutSSO({
                destinationUrl: url,
                assocHandle: deviceContext.ssoConfig.assocHandle,
                appInfo: {
                    applicationName: 'KindleForiOS',
                    applicationContext: 'KindleSSOLinkOut'
                }
            });

            if (linkoutSSOResponse.result) {
                metrics.recordMetric(`${buttonName}.LinkoutSSO.Success`, 1);
                isLinkOutWithSsoSuccessful = true;
            } else {
                metrics.recordMetric(`${buttonName}.LinkoutSSOAttempt.Failed:ErrorCode:${linkoutSSOResponse.errorCode}`, 1);
                metrics.recordMetric(`${buttonName}.LinkoutSSOAttempt.Failed.ErrorMessage:${linkoutSSOResponse.errorMessage}`, 1);
            }
        } catch (error) {
            metrics.recordMetric(`${buttonName}.LinkoutSSO.Error:${error}`, 1);
        } 
        
        setIsLoading(false);
        return isLinkOutWithSsoSuccessful;
    };

    useImpressionCallback(a11yTarget, () => metrics.recordMetricOnce(`${buttonName}.Render`, 1));
    
    const reKindleFlow = useCallback(async () => {
        metrics.recordMetric(`${buttonName}.click`, 1);

        if (isTeenAccount) {
            metrics.recordMetric(`${buttonName}.TeenAccountFiltered`, 1);
            NativeAPI.openTeenAccountError(metadata);
            return;
        }

        let success: boolean = false;

        if (isReKindle2) {
            success = NativeAPI.openExternalAccountLink(metadata);
        } else {
            // https://console.harmony.a2z.com/mshop-native-integration/Deeplinks/deeplinks/#how-to-disable-deeplinking-for-a-specific-url
            const url = getDetailsUrl(getCurrentBaseUrl(), metadata.asin, "rekindleQV") + "&nodl=0";
            success = await tryLinkOutWithSSO(url); 
            
            if (!success) {
                success = callReKindleAPI(url);
            }
        }
        
        if (!success) {
            showReKindleErrorCX();
            return;
        }

        metrics.recordMetric(`${buttonName}.InterstitialInvoked`, 1);
    }, [buttonName, isReKindle2, isTeenAccount, metadata, metrics, requestA11yFocus, workflowContext, deviceContext.ssoConfig]);

    if (deviceContext.enableRekindle2) {
        const amazonDomain = `A${deviceContext.domain.slice(1)}`;
        return (
            <div className={styles.reKindle2Container}>
                {!isMiniHeader && (
                    <div className={styles.reKindle2Upsell}>{translations.getText("rekindle2-upsell")}</div>
                )}
                <button type="button" className={styles.reKindleButton} onClick={reKindleFlow} ref={a11yTarget}>
                    <div className={styles.label} aria-label={`${amazonDomain} / Get Book`}>
                        {`${amazonDomain}/getbook`}
                    </div>
                </button>
            </div>
        );
    }

    return (
        <button type="button" className={styles.reKindleButton} onClick={reKindleFlow} ref={a11yTarget} disabled={isLoading}>
            {isLoading ? (
                <span><Spinner size="min(1rem, 28px)" /></span>
            ) : (
                <div className={styles.label}>
                    {translations.getText("rekindle-cta")}
                </div>
            )}
        </button>
    );
};

export default ReKindleButton;
